/**
 * Measured facts about a rendered frame.
 *
 * The planner is given these numbers alongside the image itself. A language
 * model reading a JPEG cannot tell 0.5% highlight clipping from 6%, and it
 * cannot see that the shadows sit at 3% rather than 11%. Handing it the actual
 * measurements is what turns "make it brighter" into a specific, defensible
 * exposure move — and it is what lets the UI show the same numbers the planner
 * reasoned from.
 */

export interface ChannelStats {
  /** Mean of the channel in 0..1 display space. */
  mean: number;
  /** Fraction of pixels at or above 0.996. */
  clippedHigh: number;
  /** Fraction of pixels at or below 0.004. */
  clippedLow: number;
}

export interface ZoneStats {
  /** Fraction of the frame in this luminance zone. */
  share: number;
  /** Mean luminance inside the zone. */
  mean: number;
}

export interface HueBin {
  /** Bin centre in degrees. */
  hue: number;
  /** Share of reasonably saturated pixels falling in this bin. */
  share: number;
  /** Mean saturation of pixels in this bin. */
  saturation: number;
}

export interface ImageStats {
  /** 64-bin luminance histogram, normalised so the bins sum to 1. */
  luminance: number[];
  /** 64-bin per-channel histograms, normalised. */
  histograms: { red: number[]; green: number[]; blue: number[] };
  channels: { red: ChannelStats; green: ChannelStats; blue: ChannelStats };

  /** Mean display luminance of the whole frame, 0..1. */
  meanLuminance: number;
  /** Median display luminance, 0..1. */
  medianLuminance: number;
  /** 1st and 99th percentile luminance — the practical black and white points. */
  blackPoint: number;
  whitePoint: number;
  /** Standard deviation of luminance: a blunt contrast measure. */
  contrast: number;

  zones: {
    shadows: ZoneStats;
    midtones: ZoneStats;
    highlights: ZoneStats;
  };

  /** Fraction of the frame with all channels clipped high / low. */
  clippedHighlights: number;
  clippedShadows: number;

  /** Mean saturation across the frame, 0..1. */
  meanSaturation: number;
  /** Dominant hues, strongest first, at most 5. */
  dominantHues: HueBin[];
  /**
   * Average R:G:B ratio of the brightest neutral-ish pixels. A ratio far from
   * 1:1:1 is the signature of a white-balance error.
   */
  neutralBalance: { r: number; g: number; b: number };

  /**
   * Share of pixels whose hue and saturation fall in the broad skin-tone
   * envelope, and their mean luminance. Used to protect skin, not to identify
   * anyone: it is a colour-range measurement, not face detection.
   */
  skinTone: { share: number; meanLuminance: number; meanSaturation: number } | null;

  /** Fraction of pixels above a local-gradient threshold: a sharpness proxy. */
  detailDensity: number;
}

const BINS = 64;

function percentile(histogram: number[], fraction: number): number {
  let cumulative = 0;
  for (let i = 0; i < histogram.length; i += 1) {
    cumulative += histogram[i];
    if (cumulative >= fraction) return (i + 0.5) / histogram.length;
  }
  return 1;
}

function rgbToHsv(r: number, g: number, b: number): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let hue = 0;
  if (delta > 1e-6) {
    if (max === r) hue = 60 * (((g - b) / delta) % 6);
    else if (max === g) hue = 60 * ((b - r) / delta + 2);
    else hue = 60 * ((r - g) / delta + 4);
  }
  if (hue < 0) hue += 360;
  const saturation = max <= 1e-6 ? 0 : delta / max;
  return [hue, saturation, max];
}

/**
 * Computes statistics from an already-rendered RGBA byte buffer — normally the
 * same downscaled proxy that is sent to the planner, so the numbers and the
 * picture always describe the same frame.
 */
export function computeImageStats(
  pixels: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): ImageStats {
  const luminance = new Array<number>(BINS).fill(0);
  const redHistogram = new Array<number>(BINS).fill(0);
  const greenHistogram = new Array<number>(BINS).fill(0);
  const blueHistogram = new Array<number>(BINS).fill(0);
  const hueBins = new Array<number>(36).fill(0);
  const hueSaturation = new Array<number>(36).fill(0);

  let count = 0;
  let sumLuminance = 0;
  let sumSaturation = 0;
  let sumSquaredLuminance = 0;
  let clippedHigh = 0;
  let clippedLow = 0;
  const clipHighChannel = [0, 0, 0];
  const clipLowChannel = [0, 0, 0];
  const channelSums = [0, 0, 0];

  let neutralR = 0;
  let neutralG = 0;
  let neutralB = 0;
  let neutralCount = 0;

  let skinCount = 0;
  let skinLuminance = 0;
  let skinSaturation = 0;

  let shadowShare = 0;
  let shadowSum = 0;
  let midShare = 0;
  let midSum = 0;
  let highShare = 0;
  let highSum = 0;

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i] / 255;
    const g = pixels[i + 1] / 255;
    const b = pixels[i + 2] / 255;

    const y = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const bin = Math.min(BINS - 1, Math.floor(y * BINS));
    luminance[bin] += 1;
    redHistogram[Math.min(BINS - 1, Math.floor(r * BINS))] += 1;
    greenHistogram[Math.min(BINS - 1, Math.floor(g * BINS))] += 1;
    blueHistogram[Math.min(BINS - 1, Math.floor(b * BINS))] += 1;

    channelSums[0] += r;
    channelSums[1] += g;
    channelSums[2] += b;
    if (r >= 0.996) clipHighChannel[0] += 1;
    if (g >= 0.996) clipHighChannel[1] += 1;
    if (b >= 0.996) clipHighChannel[2] += 1;
    if (r <= 0.004) clipLowChannel[0] += 1;
    if (g <= 0.004) clipLowChannel[1] += 1;
    if (b <= 0.004) clipLowChannel[2] += 1;
    if (r >= 0.996 && g >= 0.996 && b >= 0.996) clippedHigh += 1;
    if (r <= 0.004 && g <= 0.004 && b <= 0.004) clippedLow += 1;

    const [hue, saturation] = rgbToHsv(r, g, b);
    sumSaturation += saturation;

    if (saturation > 0.12 && y > 0.05 && y < 0.97) {
      const hueBin = Math.min(35, Math.floor(hue / 10));
      hueBins[hueBin] += 1;
      hueSaturation[hueBin] += saturation;
    }

    // Bright, near-neutral pixels carry the white-balance signal.
    if (saturation < 0.12 && y > 0.35 && y < 0.95) {
      neutralR += r;
      neutralG += g;
      neutralB += b;
      neutralCount += 1;
    }

    // Broad skin envelope: warm hues, moderate saturation, mid luminance.
    if (hue >= 5 && hue <= 50 && saturation >= 0.12 && saturation <= 0.68 && y > 0.12 && y < 0.95) {
      skinCount += 1;
      skinLuminance += y;
      skinSaturation += saturation;
    }

    if (y < 0.25) {
      shadowShare += 1;
      shadowSum += y;
    } else if (y < 0.7) {
      midShare += 1;
      midSum += y;
    } else {
      highShare += 1;
      highSum += y;
    }

    sumLuminance += y;
    sumSquaredLuminance += y * y;
    count += 1;
  }

  if (count === 0) count = 1;

  const normalise = (histogram: number[]) => histogram.map((v) => v / count);
  const luminanceNormalised = normalise(luminance);
  const mean = sumLuminance / count;

  const dominantHues: HueBin[] = hueBins
    .map((value, index) => ({
      hue: index * 10 + 5,
      share: value / count,
      saturation: value > 0 ? hueSaturation[index] / value : 0,
    }))
    .filter((bin) => bin.share > 0.005)
    .sort((a, b) => b.share - a.share)
    .slice(0, 5);

  const detailDensity = estimateDetailDensity(pixels, width, height);

  return {
    luminance: luminanceNormalised.map(round4),
    histograms: {
      red: normalise(redHistogram).map(round4),
      green: normalise(greenHistogram).map(round4),
      blue: normalise(blueHistogram).map(round4),
    },
    channels: {
      red: {
        mean: round4(channelSums[0] / count),
        clippedHigh: round4(clipHighChannel[0] / count),
        clippedLow: round4(clipLowChannel[0] / count),
      },
      green: {
        mean: round4(channelSums[1] / count),
        clippedHigh: round4(clipHighChannel[1] / count),
        clippedLow: round4(clipLowChannel[1] / count),
      },
      blue: {
        mean: round4(channelSums[2] / count),
        clippedHigh: round4(clipHighChannel[2] / count),
        clippedLow: round4(clipLowChannel[2] / count),
      },
    },
    meanLuminance: round4(mean),
    medianLuminance: round4(percentile(luminanceNormalised, 0.5)),
    blackPoint: round4(percentile(luminanceNormalised, 0.01)),
    whitePoint: round4(percentile(luminanceNormalised, 0.99)),
    contrast: round4(Math.sqrt(Math.max(0, sumSquaredLuminance / count - mean * mean))),
    zones: {
      shadows: { share: round4(shadowShare / count), mean: round4(shadowShare ? shadowSum / shadowShare : 0) },
      midtones: { share: round4(midShare / count), mean: round4(midShare ? midSum / midShare : 0) },
      highlights: { share: round4(highShare / count), mean: round4(highShare ? highSum / highShare : 0) },
    },
    clippedHighlights: round4(clippedHigh / count),
    clippedShadows: round4(clippedLow / count),
    meanSaturation: round4(sumSaturation / count),
    dominantHues: dominantHues.map((bin) => ({
      hue: bin.hue,
      share: round4(bin.share),
      saturation: round4(bin.saturation),
    })),
    neutralBalance:
      neutralCount > 32
        ? {
            r: round4(neutralR / neutralCount),
            g: round4(neutralG / neutralCount),
            b: round4(neutralB / neutralCount),
          }
        : { r: 0, g: 0, b: 0 },
    skinTone:
      skinCount / count > 0.02
        ? {
            share: round4(skinCount / count),
            meanLuminance: round4(skinLuminance / skinCount),
            meanSaturation: round4(skinSaturation / skinCount),
          }
        : null,
    detailDensity: round4(detailDensity),
  };
}

function estimateDetailDensity(
  pixels: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number {
  if (width < 3 || height < 3) return 0;
  let edges = 0;
  let samples = 0;
  const stride = width * 4;
  // Sample every other pixel: this is a texture proxy, not a measurement.
  for (let y = 1; y < height - 1; y += 2) {
    for (let x = 1; x < width - 1; x += 2) {
      const index = y * stride + x * 4;
      const centre = pixels[index] * 0.299 + pixels[index + 1] * 0.587 + pixels[index + 2] * 0.114;
      const right = pixels[index + 4] * 0.299 + pixels[index + 5] * 0.587 + pixels[index + 6] * 0.114;
      const down =
        pixels[index + stride] * 0.299 +
        pixels[index + stride + 1] * 0.587 +
        pixels[index + stride + 2] * 0.114;
      const gradient = Math.abs(centre - right) + Math.abs(centre - down);
      if (gradient > 12) edges += 1;
      samples += 1;
    }
  }
  return samples ? edges / samples : 0;
}

function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}

/**
 * A compact, token-cheap rendering of the statistics for the planner prompt.
 * Full 64-bin histograms are not sent; the derived facts are what the model
 * can actually reason about.
 */
export function summariseStatsForPlanner(stats: ImageStats): Record<string, unknown> {
  return {
    exposure_placement: {
      mean_luminance: stats.meanLuminance,
      median_luminance: stats.medianLuminance,
      black_point_p01: stats.blackPoint,
      white_point_p99: stats.whitePoint,
      contrast_stddev: stats.contrast,
    },
    clipping: {
      highlights_all_channels: stats.clippedHighlights,
      shadows_all_channels: stats.clippedShadows,
      per_channel_high: {
        r: stats.channels.red.clippedHigh,
        g: stats.channels.green.clippedHigh,
        b: stats.channels.blue.clippedHigh,
      },
    },
    tonal_distribution: {
      shadow_share: stats.zones.shadows.share,
      midtone_share: stats.zones.midtones.share,
      highlight_share: stats.zones.highlights.share,
    },
    colour: {
      mean_saturation: stats.meanSaturation,
      dominant_hues_deg: stats.dominantHues.map((h) => ({
        hue: h.hue,
        share: h.share,
        saturation: h.saturation,
      })),
      bright_neutral_rgb_ratio: stats.neutralBalance,
    },
    skin_range: stats.skinTone,
    detail_density: stats.detailDensity,
  };
}
